export function isAudioAtEndOfTrack(audio: HTMLAudioElement): boolean {
	const { duration, currentTime } = audio;
	if (
		Number.isFinite(duration) &&
		duration > 0 &&
		currentTime < duration - 0.05
	) {
		return false;
	}
	return (
		audio.ended ||
		(Number.isFinite(duration) &&
			duration > 0 &&
			currentTime >= duration - 0.05)
	);
}

export function readAudioCurrentTimeSeconds(
	audio: HTMLAudioElement | null | undefined,
): number {
	if (!audio || !Number.isFinite(audio.currentTime)) {
		return 0;
	}
	return Math.max(0, audio.currentTime);
}

export function resolveAudioProgressPercent(
	audio: HTMLAudioElement | null | undefined,
): number {
	if (!audio) {
		return 0;
	}
	const { duration, currentTime } = audio;
	if (!Number.isFinite(duration) || duration <= 0) {
		return 0;
	}
	return Math.max(0, Math.min(100, (currentTime / duration) * 100));
}

export function isAudioAtStart(audio: HTMLAudioElement): boolean {
	const { duration, currentTime } = audio;
	if (!Number.isFinite(duration) || duration <= 0) {
		return currentTime <= 0;
	}
	return currentTime < 0.05;
}

export function isReplaySeekPending(audio: HTMLAudioElement): boolean {
	if (audio.seeking) {
		return true;
	}

	const { duration, currentTime } = audio;
	if (!Number.isFinite(duration) || duration <= 0) {
		return false;
	}

	return currentTime >= duration - 0.05;
}

export function isReplayPlayingFromStart(audio: HTMLAudioElement): boolean {
	return !audio.paused && !audio.ended && isAudioAtStart(audio);
}

export function prepareReplaySeek(
	audio: HTMLAudioElement,
	onSeeked: () => void,
): () => void {
	let settled = false;

	const settle = () => {
		if (settled) {
			return;
		}
		settled = true;
		audio.removeEventListener("seeked", handleSeeked);
		onSeeked();
	};

	const handleSeeked = () => {
		settle();
	};

	audio.addEventListener("seeked", handleSeeked);

	if (audio.ended) {
		audio.pause();
	}

	try {
		audio.currentTime = 0;
	} catch {
		settle();
		return () => {};
	}

	if (!audio.seeking && !isReplaySeekPending(audio)) {
		settle();
		return () => {};
	}

	if (!audio.seeking && isReplaySeekPending(audio)) {
		audio.pause();
		try {
			audio.currentTime = 0;
		} catch {
			settle();
			return () => {};
		}

		if (!audio.seeking && !isReplaySeekPending(audio)) {
			settle();
			return () => {};
		}
	}

	requestAnimationFrame(() => {
		if (settled) {
			return;
		}
		if (!isReplaySeekPending(audio)) {
			settle();
		}
	});

	return () => {
		settled = true;
		audio.removeEventListener("seeked", handleSeeked);
	};
}
